import React, { useState } from "react";
import PromoteObjective from "./PromoteObjective";
import PromoteTargeting from "./PromoteTargeting";
import PromoteBudget from "./PromoteBudget";
import PromoteFinalPreview from "./PromoteFinalPreview";
import PromoteSubmittedSuccess from "./PromoteSubmittedSuccess";
import progressFilledIcon from "../../assets/images/icons/ads-progress-filled.svg";
import progressProcessIcon from "../../assets/images/icons/ads-progress-process.svg";
import progressPendingIcon from "../../assets/images/icons/ads-progress-pending.svg";

/* No "Create New Ad" step here (unlike the general /ads flow) - this flow always auto-generates
   the ad creative from the product's own name/image/price, so there's nothing to author manually. */
const STEP_TITLES = ["Create New Ad Campaign", "Budget Allocation", "Target & Ad Placement", "Ad Final Preview"];
const TOTAL_STEPS = STEP_TITLES.length;

// Campaign/Ad Set Name are pre-filled from the product but stay editable - the real Figma
// design leaves these blank, this is just a convenience default (matches how Meta/Google Ads
// suggest a name), so the seller never has to type something from scratch.
const buildInitialFormData = (product) => ({
  campaignName: product ? `${product.product_name} Campaign` : "",
  objective: "product_listing_views",
  budgetType: "daily",
  budget: 0,
  startDate: "",
  endDate: "",
  adSetName: product ? `${product.product_name} Ad Set` : "",
  minAge: 20,
  maxAge: 50,
  gender: "all",
  interests: [],
  devices: "all",
  connectToSeller: false,
  previousBrandBuyers: false,
  placements: { searchResult: true, marketplaceFeed: true, categoryResults: true },
});

export default function PromoteListingModal({ product, onClose }) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(() => buildInitialFormData(product));
  const [submitted, setSubmitted] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);

  const updateFormData = (patch) => setFormData((prev) => ({ ...prev, ...patch }));
  const goNext = () => setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const handleSubmitted = (result) => {
    setSubmissionResult(result);
    setSubmitted(true);
  };

  const restart = () => {
    setFormData(buildInitialFormData(product));
    setStep(1);
    setSubmitted(false);
    setSubmissionResult(null);
  };

  if (!product) return null;

  // Success screen has its own layout (no step header/progress bar), matching the real
  // AdSubmittedSuccess screen's different chrome from the rest of the wizard.
  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-[95%] md:w-[750px] max-h-[90vh] overflow-y-auto relative">
          <button
            className="absolute top-4 right-4 text-gray-500 hover:text-red-500 text-xl"
            onClick={onClose}
          >
            ✕
          </button>
          <PromoteSubmittedSuccess
            product={product}
            formData={formData}
            submissionResult={submissionResult}
            onGoToAdManager={onClose}
            onCreateAnother={restart}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-[95%] md:w-[650px] max-h-[90vh] overflow-y-auto relative">
        <button
          className="absolute top-4 right-4 text-gray-500 hover:text-red-500 text-xl"
          onClick={onClose}
        >
          ✕
        </button>

        <div className="flex items-center gap-3 mb-2">
          <button
            onClick={() => (step === 1 ? onClose() : goBack())}
            className="rounded-full bg-[#e7f3ea] p-2"
          >
            ←
          </button>
          <h1 className="text-[22px] font-semibold text-black capitalize">{STEP_TITLES[step - 1]}</h1>
        </div>
        <div className="w-full h-1 bg-[#d9d9d9] rounded-full mb-4" />

        <div className="flex items-center justify-between mb-6">
          <p className="text-sm font-bold uppercase tracking-[1.1px]">
            <span className="text-black">STEP</span> <span className="text-[#095228]">{step} OF {TOTAL_STEPS}</span>
          </p>
          <div className="flex gap-2 flex-1 max-w-[340px] ml-6">
            {STEP_TITLES.map((_, i) => (
              <img
                key={i}
                src={i < step - 1 ? progressFilledIcon : i === step - 1 ? progressProcessIcon : progressPendingIcon}
                alt=""
                className="h-[7px] flex-1 min-w-0"
              />
            ))}
          </div>
        </div>

        {step === 1 && (
          <PromoteObjective product={product} formData={formData} updateFormData={updateFormData} onNext={goNext} />
        )}
        {step === 2 && (
          <PromoteBudget formData={formData} updateFormData={updateFormData} onNext={goNext} />
        )}
        {step === 3 && (
          <PromoteTargeting formData={formData} updateFormData={updateFormData} onNext={goNext} onBack={goBack} />
        )}
        {step === 4 && (
          <PromoteFinalPreview
            product={product}
            formData={formData}
            onBack={goBack}
            onSubmitted={handleSubmitted}
          />
        )}
      </div>
    </div>
  );
}
